import React from 'react';
import * as FiIcons from 'react-icons/fi';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';

const { FiBookOpen, FiInfo, FiTarget, FiLayers, FiBarChart2, FiCheck, FiTrendingUp, FiFilter, FiCheckCircle, FiAlertCircle, FiPlusCircle, FiSearch } = FiIcons;

const ItemAnalysisGlossary = () => {
  const glossaryItems = [
    {
      title: 'Basic Metadata Analysis',
      icon: FiBarChart2,
      color: 'from-blue-500 to-blue-400',
      description: 'Examines fundamental properties of your question items such as word count, character count, and type distribution. Helps identify patterns in your question bank structure and identify outliers.'
    },
    {
      title: 'Text Complexity Analysis',
      icon: FiLayers,
      color: 'from-purple-500 to-indigo-400',
      description: 'Uses readability formulas like Flesch-Kincaid Grade Level, Flesch Reading Ease, and Gunning Fog Index to estimate question difficulty based on sentence structure, word length, and syllable count. Higher scores indicate more complex text that may require higher reading proficiency.'
    },
    {
      title: 'Keyword & Term Frequency Analysis',
      icon: FiFilter,
      color: 'from-teal-500 to-green-400',
      description: 'Identifies the most frequently used words across your question bank (excluding common stop words). Useful for detecting overused concepts or potential content bias in your assessments.'
    },
    {
      title: 'Question Type Classification',
      icon: FiCheckCircle,
      color: 'from-amber-500 to-yellow-400',
      description: 'Automatically categorizes questions as MCQ, True/False, Short Answer, etc. based on pattern recognition. Helps ensure a balanced distribution of question types in your assessments.'
    },
    {
      title: 'Distractor Analysis (MCQs)',
      icon: FiPlusCircle,
      color: 'from-red-500 to-pink-400',
      description: 'Examines multiple-choice options for length consistency, potential duplicates, and other quality indicators. Essential for creating fair and effective multiple-choice questions.'
    },
    {
      title: 'Cognitive Level Analysis',
      icon: FiTarget,
      color: 'from-cyan-500 to-sky-400',
      description: 'Maps questions to Bloom\'s Taxonomy levels (Remember, Understand, Apply, Analyze, Evaluate, Create) using keyword matching. Ensures your assessment targets appropriate cognitive skills and maintains desired difficulty distribution.'
    },
    {
      title: 'Consistency & Formatting Checks',
      icon: FiCheck,
      color: 'from-emerald-500 to-green-400',
      description: 'Identifies potential formatting issues such as missing question marks, inconsistent numbering, unbalanced parentheses, and overly long lines. Improves the overall quality and professionalism of your assessment items.'
    },
    {
      title: 'Lexical Diversity',
      icon: FiTrendingUp,
      color: 'from-violet-500 to-purple-400',
      description: 'Measures vocabulary variety using the Type-Token Ratio (unique words divided by total words). Higher values indicate more diverse language use, while lower values may suggest repetitive wording.'
    },
    {
      title: 'Answer Length Prediction',
      icon: FiAlertCircle,
      color: 'from-fuchsia-500 to-pink-400',
      description: 'Estimates the expected response length for open-ended questions based on verb cues and explicit instructions. Helps ensure questions are appropriately scoped for their point value and time constraints.'
    },
    {
      title: 'Duplicate Detection',
      icon: FiSearch,
      color: 'from-orange-500 to-amber-400',
      description: 'Identifies potentially duplicate or highly similar questions using text similarity algorithms. Prevents redundancy and maintains the integrity of your question bank.'
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="bg-gradient-to-br from-white to-slate-50 rounded-xl shadow-xl border border-slate-100 p-8 relative overflow-hidden"
    >
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-blue-100/40 to-purple-100/40 rounded-full -mr-32 -mt-32 blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-br from-green-100/40 to-teal-100/40 rounded-full -ml-32 -mb-32 blur-3xl"></div>
      
      {/* Header */}
      <div className="flex items-center space-x-3 mb-8 relative z-10">
        <div className="p-3 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl shadow-lg">
          <SafeIcon icon={FiBookOpen} className="text-white text-xl" />
        </div>
        <div>
          <h2 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-700 to-indigo-700">Item Analysis Glossary</h2>
          <p className="text-slate-600">Understanding metrics and methodologies</p>
        </div>
      </div>
      
      {/* Glossary content */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 relative z-10">
        {glossaryItems.map((item, index) => (
          <motion.div
            key={item.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05, duration: 0.5 }}
            whileHover={{ y: -5, scale: 1.02 }}
            className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden border border-slate-100 group"
          >
            <div className={`h-2 bg-gradient-to-r ${item.color} w-full`}></div>
            <div className="p-6">
              <div className="flex items-start space-x-4">
                <div className={`p-3 rounded-lg bg-gradient-to-br ${item.color} text-white shadow-md group-hover:scale-110 transition-transform duration-300`}>
                  <SafeIcon icon={item.icon} className="text-lg" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-slate-800 text-lg mb-2 group-hover:text-indigo-700 transition-colors duration-300">{item.title}</h4>
                  <p className="text-slate-600 text-sm leading-relaxed">{item.description}</p>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};

export default ItemAnalysisGlossary;